import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    Link,
    Grid,
} from "@mui/material";
import { motion } from "framer-motion";

const ProfilePage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const member = location.state?.member;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (!member) {
        return (
            <Box p={4} textAlign="center">
                <Typography variant="h6" gutterBottom>
                    No member data available.
                </Typography>
                <Button variant="contained" onClick={() => navigate(-1)}>
                    Go Back
                </Button>
            </Box>
        );
    }

    return (
        <Grid
            container
            direction="column"
            sx={{
                bgcolor: "#f5f5f5",
                color: "#fff",
                minHeight: "100vh",
                overflow: "hidden",
            }}
        >
            <Grid item sx={{ p: 2 }}>
                <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    sx={{
                        color: "#000",
                        borderColor: "#000",
                        "&:hover": { borderColor: "#000" },
                    }}
                >
                    ← Back
                </Button>
            </Grid>

            <Grid
                item
                container
                sx={{
                    flex: 1,
                    overflow: "hidden",
                }}
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
            >
                {/* Left image panel */}
                <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: { xs: "center", md: "flex-start" },
                        p: 2,
                    }}
                    component={motion.div}
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <Box
                        component="img"
                        src={member.src}
                        alt={member.name}
                        sx={{
                            width: { xs: "100%", sm: "80%", md: "100%" },
                            maxWidth: 320,
                            height: { xs: "auto", md: 400 },
                            borderRadius: 3,
                            objectFit: "cover",
                            boxShadow: "0 4px 15px rgba(0,0,0,0.7)",
                        }}
                    />
                </Grid>

                {/* Right scrollable content */}
                <Grid
                    item
                    xs={12}
                    md={8}
                    sx={{
                        height: { md: "100%", xs: "auto" },
                        overflowY: { md: "auto", xs: "visible" },
                        pr: { md: 2 },
                        p: { md: 1, xs: 2 },
                        py: 2,
                        "&::-webkit-scrollbar": { width: "4px" },
                        "&::-webkit-scrollbar-thumb": { backgroundColor: "#3f51b5", borderRadius: "4px" },
                        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                    }}
                    component={motion.div}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                >
                    <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ fontFamily: "Montserrat, sans-serif",color:"#000" }}>
                        {member.name}
                    </Typography>

                    <Typography variant="h6" color="#ffff" fontWeight="bold" gutterBottom sx={{ fontFamily: "Montserrat, sans-serif",color:"#000" }}>
                        {member.position}
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 2, fontFamily: "Montserrat, sans-serif",color:"#000" }}>
                        Email:{" "}
                        <Link href={`mailto:${member.email}`} color="secondary" underline="hover">
                            {member.email}
                        </Link>
                    </Typography>

                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ fontFamily: "Montserrat, sans-serif",color:"#000" }}>
                        Qualifications:
                    </Typography>
                    <List dense sx={{ mb: 3 }}>
                        {member.qualifications?.map((qual, idx) => (
                            <ListItem key={idx} sx={{ py: 0.3, color: "#ddd" }}>
                                <ListItemText
                                    primary={qual}
                                    primaryTypographyProps={{
                                        fontFamily: "Montserrat, sans-serif",
                                        color: "#000",
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>

                    <Typography
                        variant="h6"
                        sx={{
                            mt: 3,
                            mb: 1,
                            fontWeight: "bold",
                            borderLeft: "4px solid #3f51b5",
                            pl: 2,
                            fontFamily: "Montserrat, sans-serif",
                            color: "#90caf9"
                        }}
                    >
                        Profile:
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            whiteSpace: "pre-line",
                            lineHeight: 1.5,
                            color: "#000",
                            fontSize: "1rem",
                            letterSpacing: "0.02em",
                            fontFamily: "Montserrat, sans-serif",
                            userSelect: "text",
                            px: 1,
                            backgroundColor: "rgba(63, 81, 181, 0.1)",
                            borderRadius: 1,
                        }}
                    >
                        {`${member.fullProfile1}\n\n${member.fullProfile2}\n\n${member.fullProfile3}`}
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default ProfilePage;